import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, ExternalLink, Settings2, Eye, EyeOff, Pencil } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COLORS = [
  { from: "#F97316", to: "#DC2626", name: "Orange-Red" },
  { from: "#22C55E", to: "#059669", name: "Green" },
  { from: "#3B82F6", to: "#1D4ED8", name: "Blue" },
  { from: "#8B5CF6", to: "#6D28D9", name: "Purple" },
  { from: "#EC4899", to: "#BE185D", name: "Pink" },
  { from: "#F59E0B", to: "#D97706", name: "Amber" },
  { from: "#06B6D4", to: "#0891B2", name: "Cyan" },
  { from: "#6366F1", to: "#4F46E5", name: "Indigo" },
];

const MoreServicesManagement = () => {
  const [config, setConfig] = useState({
    section_enabled: true,
    section_title: "More Services",
    services: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    id: '',
    name: '',
    description: '',
    url: '',
    icon: '🔗',
    color_from: '#3B82F6',
    color_to: '#1D4ED8',
    enabled: true,
    order: 0
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/superadmin/more-services`);
      setConfig(res.data);
    } catch (error) {
      console.error('Failed to fetch More Services config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/superadmin/more-services`, config);
      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleSectionEnabled = () => {
    setConfig(prev => ({ ...prev, section_enabled: !prev.section_enabled }));
  };

  const toggleServiceEnabled = (serviceId) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.id === serviceId ? { ...s, enabled: !s.enabled } : s
      )
    }));
  };

  const updateService = (serviceId, updates) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.id === serviceId ? { ...s, ...updates } : s
      )
    }));
  };

  const addService = () => {
    if (!newService.id || !newService.name) {
      toast.error('Service ID and Name are required');
      return;
    }

    // Check for duplicate ID
    if (config.services.some(s => s.id === newService.id)) {
      toast.error('A service with this ID already exists');
      return;
    }

    const serviceToAdd = {
      ...newService,
      order: config.services.length + 1
    };

    setConfig(prev => ({
      ...prev,
      services: [...prev.services, serviceToAdd]
    }));

    setNewService({
      id: '',
      name: '',
      description: '',
      url: '',
      icon: '🔗',
      color_from: '#3B82F6',
      color_to: '#1D4ED8',
      enabled: true,
      order: 0
    });
    setShowAddDialog(false);
    toast.success('Service added. Click Save to apply changes.');
  };

  const deleteService = (serviceId) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.filter(s => s.id !== serviceId)
    }));
    toast.success('Service removed. Click Save to apply changes.');
  };

  const moveService = (index, direction) => {
    const newServices = [...config.services];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newServices.length) return;
    
    [newServices[index], newServices[targetIndex]] = [newServices[targetIndex], newServices[index]];
    
    // Update order values
    newServices.forEach((s, i) => s.order = i + 1);
    
    setConfig(prev => ({ ...prev, services: newServices }));
  };

  const openEditDialog = (service) => {
    setEditingService({ ...service });
  };

  const saveEditedService = () => {
    if (!editingService) return;
    
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.id === editingService.id ? editingService : s
      )
    }));
    setEditingService(null);
    toast.success('Service updated. Click Save to apply changes.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">More Services</h2>
          <p className="text-muted-foreground">Configure the More Services section in Customer App</p>
        </div>
        <Button onClick={saveConfig} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Section Settings
          </CardTitle>
          <CardDescription>Control visibility and title of the More Services section</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Show More Services Section</Label>
              <p className="text-sm text-muted-foreground">
                {config.section_enabled ? 'Section is visible to customers' : 'Section is hidden from customers'}
              </p>
            </div>
            <Switch 
              checked={config.section_enabled} 
              onCheckedChange={toggleSectionEnabled}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="section-title">Section Title</Label>
            <Input
              id="section-title"
              value={config.section_title}
              onChange={(e) => setConfig(prev => ({ ...prev, section_title: e.target.value }))}
              placeholder="More Services"
            />
          </div>
        </CardContent>
      </Card>

      {/* Services List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Services</CardTitle>
              <CardDescription>Manage individual services shown in the section</CardDescription>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Service</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-id">Service ID *</Label>
                      <Input
                        id="new-id"
                        value={newService.id}
                        onChange={(e) => setNewService(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                        placeholder="my_service"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-name">Display Name *</Label>
                      <Input
                        id="new-name"
                        value={newService.name}
                        onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="My Service"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-desc">Description</Label>
                    <Input
                      id="new-desc"
                      value={newService.description}
                      onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Short description"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-url">URL</Label>
                    <Input
                      id="new-url"
                      value={newService.url}
                      onChange={(e) => setNewService(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-icon">Icon (Emoji)</Label>
                      <Input
                        id="new-icon"
                        value={newService.icon}
                        onChange={(e) => setNewService(prev => ({ ...prev, icon: e.target.value }))}
                        placeholder="🔗"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color Scheme</Label>
                      <div className="flex gap-2 flex-wrap">
                        {DEFAULT_COLORS.map((color, i) => (
                          <button
                            key={i}
                            className={`w-8 h-8 rounded-lg border-2 ${
                              newService.color_from === color.from ? 'border-primary' : 'border-transparent'
                            }`}
                            style={{ background: `linear-gradient(135deg, ${color.from}, ${color.to})` }}
                            onClick={() => setNewService(prev => ({ 
                              ...prev, 
                              color_from: color.from, 
                              color_to: color.to 
                            }))}
                            title={color.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={newService.enabled}
                      onCheckedChange={(checked) => setNewService(prev => ({ ...prev, enabled: checked }))}
                    />
                    <Label>Enabled</Label>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={addService}>Add Service</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {config.services.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No services configured. Click "Add Service" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {config.services.map((service, index) => (
                <div 
                  key={service.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    service.enabled ? 'bg-card' : 'bg-muted/50 opacity-60'
                  }`}
                >
                  {/* Drag Handle */}
                  <div className="flex flex-col gap-1">
                    <button 
                      onClick={() => moveService(index, 'up')}
                      disabled={index === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button 
                      onClick={() => moveService(index, 'down')}
                      disabled={index === config.services.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  
                  {/* Service Icon */}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${service.color_from}, ${service.color_to})` }}
                  >
                    {service.icon}
                  </div>
                  
                  {/* Service Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-foreground">{service.name}</h4>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                        {service.id}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {service.description || 'No description'}
                    </p>
                    {service.url && (
                      <a 
                        href={service.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                      >
                        {service.url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={service.enabled}
                      onCheckedChange={() => toggleServiceEnabled(service.id)}
                    />
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openEditDialog(service)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteService(service.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Preview
          </CardTitle>
          <CardDescription>How the section will appear in the Customer App</CardDescription>
        </CardHeader>
        <CardContent>
          {!config.section_enabled ? (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <EyeOff className="w-5 h-5" />
              Section is hidden from customers
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border p-4 max-w-md">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span>✨</span>
                {config.section_title}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {config.services.filter(s => s.enabled).map(service => (
                  <div 
                    key={service.id}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 hover:shadow-md transition-all cursor-pointer"
                    style={{ 
                      backgroundColor: `${service.color_from}10`,
                      borderColor: `${service.color_from}40`
                    }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${service.color_from}, ${service.color_to})` }}
                    >
                      {service.icon}
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-sm">{service.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {service.description}
                      </p>
                    </div>
                  </div>
                ))}
                {config.services.filter(s => s.enabled).length === 0 && (
                  <div className="col-span-2 text-center py-4 text-muted-foreground">
                    No enabled services to display
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Service Dialog */}
      <Dialog open={!!editingService} onOpenChange={(open) => !open && setEditingService(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Service</DialogTitle>
          </DialogHeader>
          {editingService && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service ID</Label>
                  <Input value={editingService.id} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Display Name *</Label>
                  <Input
                    id="edit-name"
                    value={editingService.name}
                    onChange={(e) => setEditingService(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-desc">Description</Label>
                <Input
                  id="edit-desc"
                  value={editingService.description || ''}
                  onChange={(e) => setEditingService(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-url">URL</Label>
                <Input
                  id="edit-url"
                  value={editingService.url || ''}
                  onChange={(e) => setEditingService(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-icon">Icon (Emoji)</Label>
                  <Input
                    id="edit-icon"
                    value={editingService.icon || ''}
                    onChange={(e) => setEditingService(prev => ({ ...prev, icon: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color Scheme</Label>
                  <div className="flex gap-2 flex-wrap">
                    {DEFAULT_COLORS.map((color, i) => (
                      <button
                        key={i}
                        className={`w-8 h-8 rounded-lg border-2 ${
                          editingService.color_from === color.from ? 'border-primary' : 'border-transparent'
                        }`}
                        style={{ background: `linear-gradient(135deg, ${color.from}, ${color.to})` }}
                        onClick={() => setEditingService(prev => ({ 
                          ...prev, 
                          color_from: color.from, 
                          color_to: color.to 
                        }))}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={saveEditedService}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MoreServicesManagement;
